/**
 * LaporanEventDetail - Halaman detail laporan event (view only, semua user bisa akses)
 * Menampilkan list detail event dengan DataTable - tanpa Edit/Buat Laporan dan tanpa kolom Aksi
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import swal from "sweetalert";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { useParams, useNavigate } from "react-router-dom";

// @mui
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { logoutUser } from "actions/AuthAction";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

applyPlugin(jsPDF);

// Helper format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function loadLogoBase64() {
  try {
    const logoUrl = `${process.env.PUBLIC_URL || ""}/logorote.png`;
    const res = await fetch(logoUrl, { credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function addLogoToPdf(doc, logoBase64) {
  if (!logoBase64) return;
  try {
    const pageW = doc.internal.pageSize.getWidth();
    doc.addImage(logoBase64, "PNG", pageW - 26, 10, 12, 12);
  } catch (e) {
    console.warn("Logo add failed:", e);
  }
}

function LaporanEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [detailEventsList, setDetailEventsList] = useState([]);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog View PDF
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDetailEvent, setViewingDetailEvent] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // Dialog Daftar Peserta
  const [openPesertaDialog, setOpenPesertaDialog] = useState(false);
  const [daftarPeserta, setDaftarPeserta] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [selectedDetailEventId, setSelectedDetailEventId] = useState(null);
  const [downloadingPesertaPdf, setDownloadingPesertaPdf] = useState(false);

  // Dialog Foto
  const [openFotoDialog, setOpenFotoDialog] = useState(false);
  const [viewingFotos, setViewingFotos] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      logoutUser(navigate);
    }
  }, [navigate]);

  const fetchEventDetail = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const dinasIdUser = user.dinas_id;
      const currentYear = new Date().getFullYear();
      const detailUrl = dinasIdUser
        ? `${API_ESA_OFFICE}events/detail/all?dinasId=${encodeURIComponent(dinasIdUser)}&tahun=${currentYear}`
        : `${API_ESA_OFFICE}events/${eventId}/detail`;
      const [eventRes, detailRes] = await Promise.all([
        axios.get(`${API_ESA_OFFICE}events/${eventId}`).catch(() => ({ data: null })),
        axios.get(detailUrl),
      ]);
      const eventData = eventRes.data;
      let rawDetails = detailRes.data || [];
      if (dinasIdUser) {
        rawDetails = rawDetails.filter((d) => String(d.id_events) === String(eventId));
      }
      const details = rawDetails.map((item) => ({
        ...item,
        id_pembuat: item.id_pembuat ? String(item.id_pembuat) : null,
        id_events: item.id_events || parseInt(eventId, 10),
        dinas_peserta_nama: item.dinas_peserta_nama || item.nama_dinas || null,
        kegiatan: item.kegiatan || (eventData?.kegiatan || "-"),
        status_event: item.status_event || eventData?.status_event || "-",
        jumlah_peserta: parseInt(item.jumlah_peserta, 10) || 0,
      }));
      setDetailEventsList(details);
      setEventInfo(eventData || (details[0] ? { kegiatan: details[0].kegiatan } : null));
    } catch (error) {
      console.error("Error fetching event detail:", error);
      swal({ title: "Error", text: "Gagal mengambil data event", icon: "error" });
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) fetchEventDetail();
  }, [eventId, fetchEventDetail]);

  const handleOpenFotoDialog = (detailEvent) => {
    const fotos = Array.isArray(detailEvent.foto) ? detailEvent.foto : [];
    if (!fotos.length) return;
    setViewingFotos(fotos);
    setOpenFotoDialog(true);
  };

  const handleCloseFotoDialog = () => {
    setOpenFotoDialog(false);
    setViewingFotos([]);
  };

  const handleOpenDokumen = (pathOrUrl) => {
    if (!pathOrUrl) return;
    let url = pathOrUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = url.startsWith("/") ? `${API_ESA_OFFICE_UPLOAD}${url}` : `${API_ESA_OFFICE_UPLOAD}/${url}`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const generatePDF = async (detailEvent) => {
    let pesertaList = [];
    try {
      const r = await axios.get(`${API_ESA_OFFICE}attendance/${detailEvent.id}`);
      pesertaList = r.data || [];
    } catch (e) {
      console.error("Error fetching peserta:", e);
    }
    const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
    const signatureBase64Map = {};
    await Promise.all(
      (pesertaList || []).map(async (p, idx) => {
        const sig = p.signature_file;
        if (!sig || typeof sig !== "string") return;
        const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
        try {
          const res = await fetch(imgUrl, { credentials: "include" });
          if (!res.ok) return;
          const blob = await res.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          signatureBase64Map[idx] = base64;
        } catch {}
      })
    );

    const doc = new jsPDF();
    addLogoToPdf(doc, await loadLogoBase64());
    let yPos = 20;

    const renderHtmlContent = (html, x, y, maxWidth) => {
      if (!html) return y;
      let cy = y;
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      const processNode = (node, indent = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            const lines = doc.splitTextToSize(text, maxWidth - indent * 5);
            lines.forEach((line) => {
              if (cy > 270) {
                doc.addPage();
                cy = 20;
              }
              doc.text(line, x + indent * 5, cy);
              cy += 5;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          Array.from(node.childNodes).forEach((c) => processNode(c, indent));
        }
      };
      Array.from(tmp.childNodes).forEach((c) => processNode(c));
      return cy;
    };

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Event", 105, yPos, { align: "center" });
    yPos += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${detailEvent.kegiatan || "-"}`, 14, yPos);
    yPos += 7;
    doc.text(`Detail Kegiatan: ${detailEvent.detail_kegiatan || "-"}`, 14, yPos);
    yPos += 7;
    doc.text(`Lokasi: ${detailEvent.tempat || "-"}`, 14, yPos);
    yPos += 7;
    doc.text(`Waktu Mulai: ${detailEvent.waktu_mulai ? new Date(detailEvent.waktu_mulai).toLocaleString("id-ID") : "-"}`, 14, yPos);
    yPos += 7;
    doc.text(`Waktu Selesai: ${detailEvent.waktu_finish ? new Date(detailEvent.waktu_finish).toLocaleString("id-ID") : "-"}`, 14, yPos);
    yPos += 10;

    if (detailEvent.notulen && detailEvent.notulen.trim() !== "") {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Notulen", 14, yPos);
      yPos += 7;
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      yPos = renderHtmlContent(detailEvent.notulen, 14, yPos, 180);
    }

    if (detailEvent.foto && Array.isArray(detailEvent.foto) && detailEvent.foto.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Foto Kegiatan (${detailEvent.foto.length})`, 14, yPos);
      yPos += 10;
      for (let i = 0; i < detailEvent.foto.length; i++) {
        const fotoUrl = detailEvent.foto[i];
        const fullUrl = fotoUrl.startsWith("/") ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}` : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`;
        try {
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = fullUrl;
          });
          let iw = img.width;
          let ih = img.height;
          const ratio = Math.min(180 / iw, 100 / ih);
          iw *= ratio;
          ih *= ratio;
          if (yPos + ih > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(`Foto ${i + 1}:`, 14, yPos);
          yPos += 5;
          doc.addImage(img, "JPEG", 14, yPos, iw, ih);
          yPos += ih + 10;
        } catch (e) {
          doc.text(`Foto ${i + 1}: Gagal memuat`, 14, yPos);
          yPos += 7;
        }
      }
    }

    if (pesertaList.length > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Daftar Peserta (${pesertaList.length})`, 14, yPos);
      yPos += 10;
      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;
      const headers = [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]];
      const body = pesertaList.map((p, idx) => [
        String(idx + 1),
        (p.nama_user || p.nama || "-").replace(/\r\n/g, " ").replace(/\n/g, " ").substring(0, 50),
        (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
        p.waktu ? formatDateTime(p.waktu) : "-",
        signatureBase64Map[idx] ? "" : "-",
      ]);
      doc.autoTable({
        head: headers,
        body,
        startY: yPos,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 50 }, 2: { cellWidth: 36 }, 3: { cellWidth: 34 }, 4: { cellWidth: sigColWidthMm } },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 32));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          try {
            doc.addImage(base64, "PNG", cell.x + padding, cell.y + padding, imgW, imgH);
          } catch {}
        },
      });
      yPos = doc.lastAutoTable.finalY + 5;
    }

    const blob = doc.output("blob");
    return URL.createObjectURL(blob);
  };

  const handleOpenViewDialog = async (detailEvent) => {
    setViewingDetailEvent(detailEvent);
    setOpenViewDialog(true);
    try {
      const url = await generatePDF(detailEvent);
      setPdfBlobUrl(url);
    } catch (e) {
      console.error(e);
      swal({ title: "Error", text: "Gagal membuat PDF", icon: "error" });
    }
  };

  const handleCloseViewDialog = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setOpenViewDialog(false);
    setViewingDetailEvent(null);
  };

  const handleOpenPesertaDialog = async (detailEventId, jumlahPeserta) => {
    if (jumlahPeserta === 0) return;
    setSelectedDetailEventId(detailEventId);
    setOpenPesertaDialog(true);
    setLoadingPeserta(true);
    setDaftarPeserta([]);
    try {
      const r = await axios.get(`${API_ESA_OFFICE}attendance/${detailEventId}`);
      setDaftarPeserta(r.data || []);
    } catch (e) {
      console.error(e);
      swal({ title: "Error", text: "Gagal mengambil data peserta", icon: "error" });
    } finally {
      setLoadingPeserta(false);
    }
  };

  const handleClosePesertaDialog = () => {
    setOpenPesertaDialog(false);
    setDaftarPeserta([]);
    setSelectedDetailEventId(null);
  };

  const handleDownloadPesertaPDF = useCallback(async () => {
    if (!daftarPeserta?.length) return;
    setDownloadingPesertaPdf(true);
    try {
      const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
      const signatureBase64Map = {};
      await Promise.all(
        daftarPeserta.map(async (p, idx) => {
          const sig = p.signature_file;
          if (!sig || typeof sig !== "string") return;
          const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
          try {
            const res = await fetch(imgUrl, { credentials: "include" });
            if (!res.ok) return;
            const blob = await res.blob();
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            signatureBase64Map[idx] = base64;
          } catch {}
        })
      );
      const detailEv = detailEventsList.find((d) => d.id === selectedDetailEventId);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [215, 279] });
      addLogoToPdf(doc, await loadLogoBase64());
      let yPos = 12;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Daftar Peserta Hadir", 14, yPos);
      yPos += 6;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Detail Kegiatan: ${detailEv?.detail_kegiatan || "-"}`, 14, yPos);
      yPos += 5;
      doc.text(`Tanggal: ${detailEv?.waktu_mulai ? new Date(detailEv.waktu_mulai).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-"}`, 14, yPos);
      yPos += 5;
      doc.text(`Lokasi: ${detailEv?.tempat || "-"}`, 14, yPos);
      yPos += 5;
      doc.text(`Jumlah Peserta: ${daftarPeserta.length} orang`, 14, yPos);
      yPos += 8;
      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;
      doc.autoTable({
        head: [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]],
        body: daftarPeserta.map((p, idx) => [
          String(idx + 1),
          (p.nama_user || p.nama || "").replace(/\r\n/g, " ").replace(/\n/g, " "),
          (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
          p.waktu ? formatDateTime(p.waktu) : "-",
          signatureBase64Map[idx] ? "" : "-",
        ]),
        startY: yPos,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 50 }, 2: { cellWidth: 38 }, 3: { cellWidth: 35 }, 4: { cellWidth: sigColWidthMm } },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 32));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          try {
            doc.addImage(base64, "PNG", cell.x + padding, cell.y + padding, imgW, imgH);
          } catch {}
        },
      });
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      doc.setFontSize(7);
      doc.text(`Didownload dari Esa Office Apps tanggal ${new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })} oleh ${user?.nama || "User"}`, 14, doc.internal.pageSize.getHeight() - 10);
      const safeTitle = (detailEv?.detail_kegiatan || "daftar-peserta").replace(/[^a-zA-Z0-9-_]/g, "_").substring(0, 50);
      doc.save(`daftar-peserta-${safeTitle}-${selectedDetailEventId}.pdf`);
    } catch (err) {
      swal("Error", "Gagal export PDF: " + (err?.message || "Unknown"), "error");
    } finally {
      setDownloadingPesertaPdf(false);
    }
  }, [daftarPeserta, selectedDetailEventId, detailEventsList]);

  const cellWrapSx = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    py: 1,
    boxSizing: "border-box",
  };

  const detailTableColMinSx = {
    "& thead th:nth-of-type(1), & tbody td:nth-of-type(1)": { minWidth: "100px", width: "100px" },
    "& thead th:nth-of-type(2), & tbody td:nth-of-type(2)": { minWidth: "380px", width: "380px" },
    "& thead th:nth-of-type(3), & tbody td:nth-of-type(3)": { minWidth: "220px", width: "220px" },
    "& thead th:nth-of-type(4), & tbody td:nth-of-type(4)": { minWidth: "240px", width: "240px" },
    "& thead th:nth-of-type(5), & tbody td:nth-of-type(5)": { minWidth: "240px", width: "240px" },
    "& thead th:nth-of-type(6), & tbody td:nth-of-type(6)": { minWidth: "170px", width: "170px" },
    "& thead th:nth-of-type(7), & tbody td:nth-of-type(7)": { minWidth: "130px", width: "130px" },
    "& thead th:nth-of-type(8), & tbody td:nth-of-type(8)": { minWidth: "180px", width: "180px" },
  };

  const pesertaDialogTableColMinSx = {
    "& thead th:nth-of-type(1), & tbody td:nth-of-type(1)": { minWidth: "90px", width: "90px" },
    "& thead th:nth-of-type(2), & tbody td:nth-of-type(2)": { minWidth: "280px", width: "280px" },
    "& thead th:nth-of-type(3), & tbody td:nth-of-type(3)": { minWidth: "280px", width: "280px" },
    "& thead th:nth-of-type(4), & tbody td:nth-of-type(4)": { minWidth: "180px", width: "180px" },
    "& thead th:nth-of-type(5), & tbody td:nth-of-type(5)": { minWidth: "170px", width: "170px" },
  };

  const downloadPesertaBtnSx = {
    color: "#37474f",
    borderColor: "rgba(55, 71, 79, 0.5)",
    "& .MuiButton-startIcon": { color: "#37474f" },
    "&:hover": {
      color: "#263238",
      borderColor: "#37474f",
      backgroundColor: "rgba(55, 71, 79, 0.06)",
      "& .MuiButton-startIcon": { color: "#263238" },
    },
    "&.Mui-disabled": {
      color: "rgba(0,0,0,0.38)",
      borderColor: "rgba(0,0,0,0.12)",
      "& .MuiButton-startIcon": { color: "rgba(0,0,0,0.38)" },
    },
  };

  const columns = useMemo(
    () => [
      { Header: "No", accessor: "no", width: "100px", Cell: ({ value }) => <MDBox sx={cellWrapSx}><MDTypography variant="caption">{value}</MDTypography></MDBox> },
      {
        Header: "Detail Kegiatan",
        accessor: "detail_kegiatan",
        width: "380px",
        Cell: ({ row, value }) => {
          const s = row.original.status;
          return (
            <MDBox display="flex" alignItems="flex-start" gap={1} sx={{ ...cellWrapSx, width: "100%" }}>
              <MDTypography variant="caption" color="text" sx={{ flex: 1, minWidth: 0, overflow: "hidden", wordBreak: "break-word" }}>{value || "-"}</MDTypography>
              {s === "selesai" && <CheckCircleIcon sx={{ color: "success.main", fontSize: 36, flexShrink: 0 }} />}
              {s === "proses" && <AccessTimeIcon sx={{ color: "warning.main", fontSize: 36, flexShrink: 0 }} />}
            </MDBox>
          );
        },
      },
      {
        Header: "Waktu",
        accessor: "waktu",
        width: "220px",
        Cell: ({ row }) => (
          <MDBox sx={cellWrapSx}>
            <MDTypography variant="caption" display="block">Mulai: {formatDateTime(row.original.waktu_mulai)}</MDTypography>
            <MDTypography variant="caption" display="block">Selesai: {formatDateTime(row.original.waktu_finish)}</MDTypography>
          </MDBox>
        ),
      },
      { Header: "Tempat", accessor: "tempat", width: "240px", Cell: ({ value }) => <MDBox sx={cellWrapSx}><MDTypography variant="caption">{value || "-"}</MDTypography></MDBox> },
      {
        Header: "Dinas Peserta",
        accessor: "dinas_peserta",
        width: "240px",
        Cell: ({ row }) => {
          const isEval = row.original.status_event === "evaluasi" || row.original.status_event === "asistensi";
          if (!isEval) return <MDBox sx={cellWrapSx}><MDTypography variant="caption" color="text.secondary">-</MDTypography></MDBox>;
          return <MDBox sx={cellWrapSx}><MDTypography variant="caption">{row.original.dinas_peserta_nama || row.original.nama_dinas || "-"}</MDTypography></MDBox>;
        },
      },
      {
        Header: "Jumlah Peserta",
        accessor: "jumlah_peserta",
        width: "170px",
        Cell: ({ row, value }) => {
          const n = value ?? row.original.jumlah_peserta ?? 0;
          return (
            <MDBox sx={cellWrapSx}>
              <Button variant="outlined" size="small" startIcon={<PeopleIcon />} onClick={() => handleOpenPesertaDialog(row.original.id, n)} disabled={n === 0} sx={{ textTransform: "none", color: "info.main" }}>
                Lihat ({n})
              </Button>
            </MDBox>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        width: "130px",
        Cell: ({ value }) => (
          <MDBox sx={cellWrapSx}>
            {!value ? <MDTypography variant="caption" color="text.secondary">-</MDTypography> : (
              <Chip label={value === "selesai" ? "Selesai" : "Proses"} color={value === "selesai" ? "success" : "warning"} size="small" variant="outlined" />
            )}
          </MDBox>
        ),
      },
      {
        Header: "Lapor",
        accessor: "lapor",
        width: "180px",
        Cell: ({ row }) => {
          const hasLapor = (row.original.notulen && row.original.notulen.trim() !== "") || (Array.isArray(row.original.foto) && row.original.foto.length > 0);
          return (
            <MDBox display="flex" gap={1} sx={{ ...cellWrapSx, flexWrap: "wrap" }}>
              {hasLapor && (
                <Tooltip title="Lihat PDF">
                  <IconButton color="success" size="small" onClick={() => handleOpenViewDialog(row.original)}>
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              )}
              {Array.isArray(row.original.foto) && row.original.foto.length > 0 && (
                <Tooltip title="Foto Kegiatan">
                  <IconButton color="primary" size="small" onClick={() => handleOpenFotoDialog(row.original)}>
                    <PhotoLibraryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {row.original.dokumen && (
                <Tooltip title="Buka Dokumen">
                  <IconButton color="secondary" size="small" onClick={() => handleOpenDokumen(row.original.dokumen)}>
                    {/^https?:\/\//i.test(row.original.dokumen) ? <LinkIcon fontSize="small" /> : <PictureAsPdfIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],
    []
  );

  const tableData = useMemo(() => ({
    columns,
    rows: detailEventsList.map((d, i) => ({
      id: d.id,
      id_events: d.id_events,
      no: i + 1,
      detail_kegiatan: d.detail_kegiatan || "-",
      waktu_mulai: d.waktu_mulai,
      waktu_finish: d.waktu_finish,
      tempat: d.tempat || "-",
      dinas_peserta_nama: d.dinas_peserta_nama || d.nama_dinas,
      jumlah_peserta: d.jumlah_peserta || 0,
      status: d.status || null,
      status_event: d.status_event,
      notulen: d.notulen,
      foto: d.foto,
      dokumen: d.dokumen,
    })),
  }), [detailEventsList, columns]);

  const eventName = eventInfo?.kegiatan || detailEventsList[0]?.kegiatan || "Detail Laporan Event";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" alignItems="center" gap={2} mb={2}>
              <IconButton onClick={() => navigate("/events/laporan-event")} size="small" sx={{ bgcolor: "background.paper" }}>
                <ArrowBackIcon />
              </IconButton>
              <MDTypography variant="h5">{eventName}</MDTypography>
            </MDBox>

            <Card>
              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </MDBox>
                ) : detailEventsList.length === 0 ? (
                  <MDTypography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Tidak ada detail event
                  </MDTypography>
                ) : (
                  <MDBox
                    sx={{
                      "& table": { tableLayout: "fixed", width: "100%" },
                      "& th, & td": {
                        overflow: "hidden",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      },
                      "& td > div": {
                        width: "100% !important",
                        maxWidth: "100% !important",
                        display: "block",
                        overflow: "hidden",
                      },
                      ...detailTableColMinSx,
                    }}
                  >
                    <DataTable
                      table={tableData}
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                      showTotalEntries
                      isSorted
                      noEndBorder
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog View PDF */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Lihat Laporan PDF</MDTypography>
            <IconButton onClick={handleCloseViewDialog}><CloseIcon /></IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {pdfBlobUrl ? (
              <iframe src={pdfBlobUrl} width="100%" height="600px" title="PDF" style={{ border: "none" }} />
            ) : (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
                <MDTypography variant="body2" ml={2}>Membuat PDF...</MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {pdfBlobUrl && (
            <Button onClick={() => { const a = document.createElement("a"); a.href = pdfBlobUrl; a.download = `Laporan_${viewingDetailEvent?.kegiatan || "Event"}_${Date.now()}.pdf`; a.click(); }} variant="outlined">
              Download PDF
            </Button>
          )}
          <Button onClick={handleCloseViewDialog} variant="contained" color="primary">Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Peserta */}
      <Dialog open={openPesertaDialog} onClose={handleClosePesertaDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Daftar Peserta</MDTypography>
            <MDBox display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={downloadingPesertaPdf ? <CircularProgress size={16} sx={{ color: "#37474f" }} /> : <PictureAsPdfIcon sx={{ color: "#37474f" }} />}
                onClick={handleDownloadPesertaPDF}
                disabled={!daftarPeserta?.length || downloadingPesertaPdf}
                sx={downloadPesertaBtnSx}
              >
                Download
              </Button>
              <IconButton onClick={handleClosePesertaDialog} size="small"><CloseIcon /></IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingPeserta ? (
              <MDBox display="flex" justifyContent="center" minHeight={200}><CircularProgress /></MDBox>
            ) : daftarPeserta.length === 0 ? (
              <MDTypography variant="body2" color="text.secondary" textAlign="center">Tidak ada peserta</MDTypography>
            ) : (
              <MDBox
                sx={{
                  "& table": { tableLayout: "fixed", width: "100%" },
                  "& th, & td": { overflow: "hidden", whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-word", verticalAlign: "top" },
                  "& td > div": { width: "100% !important", maxWidth: "100% !important", display: "block", overflow: "hidden" },
                  ...pesertaDialogTableColMinSx,
                }}
              >
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "90px", Cell: ({ value }) => <MDBox sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", wordBreak: "break-word", py: 1 }}><MDTypography variant="caption">{value}</MDTypography></MDBox> },
                      {
                        Header: "Nama",
                        accessor: "nama",
                        width: "280px",
                        Cell: ({ row }) => (
                          <MDBox display="flex" alignItems="center" gap={1} sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", wordBreak: "break-word", py: 1 }}>
                            {row.original.uid_RSS ? (
                              <img src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={(e) => { e.target.style.display = "none"; }} />
                            ) : (
                              <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "grey.200", color: "grey.500", flexShrink: 0 }}><PersonIcon sx={{ fontSize: 20 }} /></MDBox>
                            )}
                            <MDTypography variant="caption" color="text" sx={{ minWidth: 0, overflow: "hidden", wordBreak: "break-word" }}>{row.original.nama_user || row.original.nama || "-"}</MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Instansi",
                        accessor: "instansi",
                        width: "280px",
                        Cell: ({ value }) => (
                          <MDBox sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", wordBreak: "break-word", py: 1 }}><MDTypography variant="caption" color="text">{value || "-"}</MDTypography></MDBox>
                        ),
                      },
                      {
                        Header: "Waktu",
                        accessor: "waktu",
                        width: "180px",
                        Cell: ({ value }) => <MDBox sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", wordBreak: "break-word", py: 1 }}><MDTypography variant="caption" color="text">{value ? formatDateTime(value) : "-"}</MDTypography></MDBox>,
                      },
                      {
                        Header: "Tanda Tangan",
                        accessor: "signature_file",
                        width: "170px",
                        Cell: ({ row }) => {
                          const sig = row.original.signature_file;
                          if (!sig) return <MDBox sx={{ py: 1 }}><MDTypography variant="caption" color="text.secondary">-</MDTypography></MDBox>;
                          const imgUrl = `${API_ESA_OFFICE_UPLOAD}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
                          return (
                            <MDBox display="flex" alignItems="center" minHeight={70} sx={{ py: 1 }}>
                              <MDBox
                                component="img"
                                src={imgUrl}
                                alt="Tanda tangan"
                                sx={{ maxWidth: 100, maxHeight: 60, objectFit: "contain" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = "inline";
                                }}
                              />
                              <MDTypography variant="caption" color="text.secondary" sx={{ display: "none" }}>-</MDTypography>
                            </MDBox>
                          );
                        },
                      },
                    ],
                    rows: daftarPeserta.map((p, i) => ({
                      no: i + 1,
                      nama: p.nama_user || p.nama,
                      instansi: p.instansi,
                      waktu: p.waktu,
                      uid_RSS: p.uid_RSS,
                      nama_user: p.nama_user,
                      signature_file: p.signature_file,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePesertaDialog} variant="contained" color="primary">Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Foto Kegiatan */}
      <Dialog open={openFotoDialog} onClose={handleCloseFotoDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Foto Kegiatan</MDTypography>
            <IconButton onClick={handleCloseFotoDialog}><CloseIcon /></IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {viewingFotos?.length ? (
              <Grid container spacing={2}>
                {viewingFotos.map((url, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <img src={url.startsWith("/") ? `${API_ESA_OFFICE_UPLOAD}${url}` : `${API_ESA_OFFICE_UPLOAD}/${url}`} alt="" style={{ width: "100%", borderRadius: 8 }} onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=Tidak+Tersedia"; }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <MDTypography variant="body2" color="text.secondary">Tidak ada foto</MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFotoDialog} variant="contained" color="primary">Tutup</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default LaporanEventDetail;
